import { useState } from "react";
import { CalendarDays, Check, ChevronDown, Save, Users } from "lucide-react";
import type { DateRange, Player, SavedPool } from "../types";

type FiltersProps = {
  range: DateRange;
  players: Player[];
  pools: SavedPool[];
  selectedPlayerIds: string[];
  activePoolId: string;
  busy: boolean;
  onRangeChange: (range: DateRange) => void;
  onPlayersChange: (ids: string[]) => void;
  onPoolChange: (poolId: string) => void;
  onSavePool: (name: string) => Promise<void>;
};

const shiftDays = (date: string, amount: number): string => {
  const value = new Date(`${date}T12:00:00`);
  value.setDate(value.getDate() + amount);
  return value.toISOString().slice(0, 10);
};

export function Filters({
  range,
  players,
  pools,
  selectedPlayerIds,
  activePoolId,
  busy,
  onRangeChange,
  onPlayersChange,
  onPoolChange,
  onSavePool,
}: FiltersProps) {
  const [saving, setSaving] = useState(false);
  const [poolName, setPoolName] = useState("");

  const applyShortcut = (shortcut: "7D" | "30D" | "90D" | "ANO") => {
    if (shortcut === "ANO") {
      onRangeChange({ from: `${range.to.slice(0, 4)}-01-01`, to: range.to });
      return;
    }
    const days = Number(shortcut.replace("D", ""));
    onRangeChange({ from: shiftDays(range.to, -(days - 1)), to: range.to });
  };

  const togglePlayer = (id: string) => {
    const next = selectedPlayerIds.includes(id)
      ? selectedPlayerIds.filter((playerId) => playerId !== id)
      : [...selectedPlayerIds, id];
    onPlayersChange(next.length ? next : [id]);
  };

  const save = async () => {
    if (!poolName.trim() || selectedPlayerIds.length === 0) return;
    await onSavePool(poolName.trim());
    setPoolName("");
    setSaving(false);
  };

  const selectionLabel =
    selectedPlayerIds.length === players.length
      ? "Todos os jogadores"
      : selectedPlayerIds.length === 1
        ? players.find((player) => player.id === selectedPlayerIds[0])?.name ?? "1 jogador"
        : `${selectedPlayerIds.length} jogadores`;

  return (
    <section className="filters" aria-label="Filtros do painel">
      <div className="date-fields">
        <label>
          <span>De</span>
          <input
            type="date"
            value={range.from}
            max={range.to}
            onChange={(event) => onRangeChange({ ...range, from: event.target.value })}
          />
          <CalendarDays size={16} />
        </label>
        <label>
          <span>Até</span>
          <input
            type="date"
            value={range.to}
            min={range.from}
            onChange={(event) => onRangeChange({ ...range, to: event.target.value })}
          />
          <CalendarDays size={16} />
        </label>
      </div>

      <div className="selection-fields">
        <details className="select-menu">
          <summary>
            <Users size={16} />
            <span>{selectionLabel}</span>
            <ChevronDown size={16} />
          </summary>
          <div className="select-menu__popover">
            <button
              type="button"
              className="select-menu__all"
              onClick={() => onPlayersChange(players.map((player) => player.id))}
            >
              Selecionar todos
            </button>
            {players.map((player) => (
              <label key={player.id}>
                <input
                  type="checkbox"
                  checked={selectedPlayerIds.includes(player.id)}
                  onChange={() => togglePlayer(player.id)}
                />
                <span className="fake-checkbox">
                  {selectedPlayerIds.includes(player.id) && <Check size={12} />}
                </span>
                {player.name}
              </label>
            ))}
          </div>
        </details>

        <select value={activePoolId} onChange={(event) => onPoolChange(event.target.value)}>
          <option value="">Pools salvas</option>
          {pools.map((pool) => (
            <option key={pool.id} value={pool.id}>
              {pool.name}
            </option>
          ))}
        </select>

        {!saving ? (
          <button className="button" type="button" onClick={() => setSaving(true)}>
            <Save size={16} /> Salvar pool
          </button>
        ) : (
          <span className="save-pool">
            <input
              value={poolName}
              onChange={(event) => setPoolName(event.target.value)}
              placeholder="Nome da pool"
              autoFocus
            />
            <button className="button button--primary" type="button" onClick={save} disabled={busy}>
              Salvar
            </button>
            <button className="button" type="button" onClick={() => setSaving(false)}>
              Cancelar
            </button>
          </span>
        )}
      </div>

      <div className="shortcuts" aria-label="Atalhos de período">
        {(["7D", "30D", "90D", "ANO"] as const).map((shortcut) => (
          <button key={shortcut} type="button" onClick={() => applyShortcut(shortcut)}>
            {shortcut}
          </button>
        ))}
      </div>
    </section>
  );
}
